import { useEffect, useMemo, useState } from "react";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { usePreQuote } from "./usePreQuote";
import { useQuote } from "./useQuote";
import { loadCatalogPayers } from "./usePayerName";
import { COUNTRY_CURRENCY } from "./useExchangeRate";
import type {
  IEstimateOption,
  IEstimateResult,
  IPreQuoteRequest,
  IQuoteResponse,
  QuoteAnchor,
} from "../services/api";
import {
  availableFundingCategories,
  availableFundingMethodsFromDiscovery,
  buildClaimFundingPreQuoteInput,
  filterPreQuoteOptions,
  isFundingMethod,
  matchPayerCodeByName,
  optionForCategory as pickOptionForCategory,
  optionForFundingMethod as pickOptionForFundingMethod,
  resolvePayerCode,
  resolvePayoutMethod,
  resolveUnpinnedBankName,
  type FundingCategory,
  type FundingMethod,
} from "../utils/catalog";
import { paymentType } from "../interfaces/payment";
import { resolveAmountAnchor } from "../utils/amountAnchor";
import { resolveClaimStateCode } from "../utils/claimState";
import { classifyQuoteError, type QuoteErrorKind } from "../utils/quoteError";

export interface TransferEconomics {
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee: number;
  totalCost: number;
  /** True while the numbers come from the indicative pre-quote (not binding). */
  estimated: boolean;
}

export interface TransferPricing {
  amountAnchor: QuoteAnchor;
  sourceCountry: string;
  destinationCountry: string;
  sourceCurrency: string;
  destinationCurrency: string;
  /** Canonical payout method fixed by the claim link (e.g. `CASH_PICKUP`). */
  payoutMethod: string | undefined;
  /** Canonical payer fixed by the claim link (e.g. `ELEKTRA`), if pinned. */
  payerCode: string | undefined;
  /** Pinned amount for pricing (send or receive leg per `amountAnchor`). */
  amount: number | null;

  preQuoteLoading: boolean;
  preQuoteError: Error | null;
  routeUnavailable: boolean;
  options: IEstimateOption[];
  availableCategories: Set<FundingCategory>;
  availableFundingMethods: FundingMethod[];
  optionForCategory: (category: FundingCategory) => IEstimateOption | undefined;
  bestOption: IEstimateOption | null;
  /** Pre-collapse rails; used to harvest limits corridors (D20). */
  facets: IEstimateResult["facets"];

  selectedOption: IEstimateOption | null;
  selectedFundingMethod: string | null;
  capabilityKey: string | null;

  quote: IQuoteResponse | null;
  quoteLoading: boolean;
  quoteError: Error | null;
  /** `route` means this funding selection cannot be quoted; `retryable` can be fetched again. */
  quoteErrorKind: QuoteErrorKind | null;
  refetchQuote: () => void;
  isExpiringSoon: boolean;
  timeRemaining: number;

  economics: TransferEconomics | null;
}

/**
 * Single source of truth for the fund-request quote screen pricing.
 */
export const useTransferPricing = (): TransferPricing => {
  const { paymentState, pendingFundingMethod } = useStore();
  const { session } = useSessionStore();

  const config = session.data?.config;
  const userData = session.data?.userData;

  const amountAnchor = resolveAmountAnchor(config);

  /** Payout pin: config projection, then recipient DTO, then claimer (unused). */
  const payoutUserData = useMemo(() => {
    const paymentMethod =
      config?.userData?.paymentMethod ??
      config?.recipient?.payoutMethod ??
      userData?.paymentMethod;
    if (!paymentMethod) return userData;
    return {
      referenceId: userData?.referenceId ?? "",
      paymentMethod,
      name: userData?.name,
      fullName: userData?.fullName,
    };
  }, [config?.userData?.paymentMethod, config?.recipient?.payoutMethod, userData]);

  const corridor = config?.defaultCorridor || config?.corridors?.[0];
  const sourceCountry = corridor?.originCountryCode || "US";
  const destinationCountry = corridor?.destinationCountryCode || "MX";
  const sourceCurrency =
    config?.sourceCurrency || COUNTRY_CURRENCY[sourceCountry] || "USD";
  const destinationCurrency =
    config?.destinationCurrency ||
    COUNTRY_CURRENCY[destinationCountry] ||
    "MXN";

  const payoutMethod = resolvePayoutMethod(payoutUserData);
  const claimPayerCode = resolvePayerCode(payoutUserData);
  const claimStateCode = resolveClaimStateCode(config?.recipient);
  const unpinnedBankName = resolveUnpinnedBankName(payoutUserData);

  // A bank payout minted with only `bankName` still names a payer. Map it to a
  // catalog payer code; no match means the claimed bank cannot be served.
  const [bankPayer, setBankPayer] = useState<{
    bankName: string;
    status: "resolved" | "unmatched" | "error";
    payerCode?: string;
  } | null>(null);

  useEffect(() => {
    if (!unpinnedBankName) return;
    let active = true;
    loadCatalogPayers(destinationCountry, "BANK_ACCOUNT")
      .then((items) => {
        if (!active) return;
        const code = matchPayerCodeByName(unpinnedBankName, items);
        setBankPayer(
          code
            ? { bankName: unpinnedBankName, status: "resolved", payerCode: code }
            : { bankName: unpinnedBankName, status: "unmatched" }
        );
      })
      .catch(() => {
        if (active) setBankPayer({ bankName: unpinnedBankName, status: "error" });
      });
    return () => {
      active = false;
    };
  }, [unpinnedBankName, destinationCountry]);

  const bankResolution =
    unpinnedBankName && bankPayer?.bankName === unpinnedBankName ? bankPayer : null;
  const bankResolving = Boolean(unpinnedBankName) && !bankResolution;
  const bankUnmatched = bankResolution?.status === "unmatched";
  const payerCode = claimPayerCode ?? bankResolution?.payerCode;

  // Lola pins amount + anchor in the claim; the user does not retype it.
  const amount = useMemo(() => {
    const pinned = config?.amount;
    return typeof pinned === "number" && pinned > 0 ? pinned : null;
  }, [config?.amount]);

  const quoteCurrency =
    amountAnchor === "receive" ? destinationCurrency : sourceCurrency;

  // Payout (+ payer) is frozen by the claim; pin payerCode before
  // vary=fundingMethod collapse so cheapest-per-funding stays within that
  // network (ELEKTRA ≡ MX_ELEKTRA server-side).
  const preQuoteInput = useMemo<IPreQuoteRequest | null>(() => {
    if (!amount || bankResolving || bankUnmatched) return null;
    return buildClaimFundingPreQuoteInput({
      amount,
      amountAnchor,
      originCountryCode: sourceCountry,
      destinationCountryCode: destinationCountry,
      sourceCurrency,
      destinationCurrency,
      payoutMethod,
      payerCode,
      stateCode: claimStateCode,
    });
  }, [
    amount,
    amountAnchor,
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    payoutMethod,
    payerCode,
    claimStateCode,
    bankResolving,
    bankUnmatched,
  ]);

  const {
    result: preQuote,
    loading: preQuoteFetching,
    error: preQuoteError,
  } = usePreQuote(preQuoteInput);
  const preQuoteLoading = preQuoteFetching || (bankResolving && amount != null);

  const options = useMemo(
    () => filterPreQuoteOptions(preQuote?.options, payoutMethod, payerCode),
    [preQuote, payoutMethod, payerCode]
  );

  const routeUnavailable =
    amount != null && (bankUnmatched || (!!preQuote && options.length === 0));

  const categories = useMemo(
    () => availableFundingCategories(options),
    [options]
  );

  const bestOption = useMemo<IEstimateOption | null>(() => {
    if (options.length === 0) return null;
    return [...options].sort((a, b) => a.sendAmount - b.sendAmount)[0];
  }, [options]);

  const optionForCategory = useMemo(
    () => (category: FundingCategory) => pickOptionForCategory(options, category),
    [options]
  );

  const fundingMethods = useMemo(
    () =>
      availableFundingMethodsFromDiscovery({
        facetFundingMethods: preQuote?.facets?.fundingMethods,
        options,
      }),
    [options, preQuote?.facets?.fundingMethods]
  );

  const selectedOption = useMemo<IEstimateOption | null>(() => {
    if (!isFundingMethod(pendingFundingMethod)) return null;
    return pickOptionForFundingMethod(options, pendingFundingMethod) ?? null;
  }, [options, pendingFundingMethod]);

  const selectedFundingMethod = selectedOption?.fundingMethod ?? null;
  const capabilityKey = selectedOption?.capabilityKey ?? null;

  const cardId =
    paymentState.payment?.type === paymentType.CARD
      ? paymentState.payment?.cardId ?? null
      : null;

  const {
    quote,
    loading: quoteLoading,
    error: quoteError,
    refetch: refetchQuote,
    isExpiringSoon,
    timeRemaining,
  } = useQuote({
    sourceCountry,
    destinationCountry,
    fundingMethod: selectedFundingMethod,
    payoutMethod: payoutMethod ?? null,
    capabilityKey,
    amount,
    currency: quoteCurrency,
    destinationCurrency,
    anchor: amountAnchor,
    cardId,
  });

  const economics = useMemo<TransferEconomics | null>(() => {
    if (quote) {
      return {
        sendAmount: quote.sendAmount,
        receiveAmount: quote.receiveAmount,
        exchangeRate: quote.exchangeRate,
        fee: quote.totalFee ?? quote.fee ?? 0,
        totalCost: quote.totalCost,
        estimated: false,
      };
    }
    const option = selectedOption ?? bestOption;
    if (option) {
      return {
        sendAmount: option.sendAmount,
        receiveAmount: option.receiveAmount,
        exchangeRate: option.customerFxRate,
        fee: option.customerFee,
        totalCost: option.sendAmount + option.customerFee,
        estimated: true,
      };
    }
    return null;
  }, [quote, selectedOption, bestOption]);

  return {
    amountAnchor,
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    payoutMethod,
    payerCode,
    amount,
    preQuoteLoading,
    preQuoteError,
    routeUnavailable,
    options,
    availableCategories: categories,
    availableFundingMethods: fundingMethods,
    optionForCategory,
    bestOption,
    facets: preQuote?.facets,
    selectedOption,
    selectedFundingMethod,
    capabilityKey,
    quote,
    quoteLoading,
    quoteError,
    quoteErrorKind: quoteError ? classifyQuoteError(quoteError) : null,
    refetchQuote,
    isExpiringSoon,
    timeRemaining,
    economics,
  };
};
